import { useEffect, useRef, useState } from "react";

/**
 * The three My Child posters, swapped on a randomised interval.
 *
 * "Every five minutes on average" is taken literally: each wait is drawn
 * uniformly from [0.5x, 1.5x] of five minutes, so the mean is exactly five
 * minutes while no two swaps can land seconds apart. An exponential draw would
 * also average five minutes, but its short tail would occasionally flash two
 * posters in a second, which is not what anyone means by this.
 *
 * Most visits are far shorter than one interval, so the poster a visitor sees
 * is decided by the random FIRST pick, not by the rotation. The rotation is for
 * whoever stays.
 */
export const MEAN_MS = 5 * 60_000;

export type Poster = { slug: string; alt: string };

export function nextDelay(random: () => number = Math.random) {
  return MEAN_MS * (0.5 + random());
}

export function pickOther(current: number, count: number, random: () => number = Math.random) {
  if (count < 2) return current;
  const offset = 1 + Math.floor(random() * (count - 1));
  return (current + offset) % count;
}

export function PosterRotator({ posters }: { posters: Poster[] }) {
  // Random per visit, so two people opening the page see different key art.
  const [active, setActive] = useState(() => Math.floor(Math.random() * posters.length));
  // Only what has actually been shown is in the DOM; the rest are never fetched
  // for the (many) visitors who leave before the first swap.
  const [mounted, setMounted] = useState<number[]>([]);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    setMounted((m) => (m.includes(active) ? m : [...m, active]));
  }, [active]);

  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (posters.length < 2) return;
    // Someone who asked for less motion gets one poster, not a rotation.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (reduced?.matches) return;

    let cancelled = false;

    const schedule = () => {
      window.clearTimeout(timer.current);
      // Choose the next poster NOW and warm it, rather than at swap time: the
      // preload and the state change would otherwise land in the same tick and
      // the crossfade would be a fade to an empty box on a slow connection.
      const next = pickOther(activeRef.current, posters.length);
      const incoming = posters[next];
      if (incoming) new Image().src = `/art/film/poster-${incoming.slug}.jpg`;

      timer.current = window.setTimeout(() => {
        if (cancelled) return;
        setActive(next);
        schedule();
      }, nextDelay());
    };

    const onVisibility = () => {
      if (document.hidden) window.clearTimeout(timer.current);
      else schedule();
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [posters]);

  return (
    <div className="poster" aria-live="off">
      {posters.map((p, i) =>
        mounted.includes(i) ? (
          <img
            key={p.slug}
            className={`poster__img${i === active ? " is-active" : ""}`}
            src={`/art/film/poster-${p.slug}.jpg`}
            srcSet={`/art/film/poster-${p.slug}.jpg 900w, /art/film/poster-${p.slug}@2x.jpg 1800w`}
            sizes="(max-width: 560px) 88vw, 440px"
            alt={i === active ? p.alt : ""}
            aria-hidden={i === active ? undefined : true}
            width={900}
            height={1350}
            decoding="async"
            fetchPriority={i === active ? "high" : "low"}
          />
        ) : null,
      )}
    </div>
  );
}
